import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createOwnership } from "@/helpers/apis";
import {
  OwnershipRulesFormSchema,
  OwnershipRulesFormValues,
  OwnershipEntities,
  OwnershipConditionKeys,
  OwnershipConditionTypes,
} from "./types";
import { toast } from "@altimateai/lego";
import { buildOwnershipRequest } from "./ownershipRequestBuilder";
import { getConditionKeysForEntity, getConditionTypesForKey } from "./ownershipHelpers";

const DEFAULT_OWNERSHIP = {
  entity_type: OwnershipEntities.WAREHOUSE,
  condition_key: OwnershipConditionKeys.WAREHOUSE_NAME,
  condition_type: OwnershipConditionTypes.EQUALS,
  condition_value: "",
  condition_key_value: "",
};

export const useOwnershipForm = (groupId: string, onSuccess: () => void) => {
  const queryClient = useQueryClient();

  const form = useForm<OwnershipRulesFormValues>({
    resolver: zodResolver(OwnershipRulesFormSchema),
    defaultValues: {
      ownerships: [DEFAULT_OWNERSHIP],
    },
  });

  const { fields, append, remove } = useFieldArray({
    name: "ownerships",
    control: form.control,
  });

  const { mutate: createRules, isPending: isLoading } = useMutation({
    mutationFn: async (data: OwnershipRulesFormValues) => {
      const payload = buildOwnershipRequest(data.ownerships);
      await createOwnership(groupId, payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["UserGroupDetails", groupId] });
      toast({ description: "Ownership rules created successfully" });
      onSuccess();
      form.reset({ ownerships: [DEFAULT_OWNERSHIP] });
    },
    onError: (error: Error) => {
      toast({
        description: `Failed to create ownership rules: ${error.message || error}`,
        variant: "destructive",
      });
    },
  });

  const validateForm = async (): Promise<boolean> => {
    return form.trigger();
  };

  const addOwnership = () => {
    const defaultEntityType = OwnershipEntities.WAREHOUSE;
    const availableKeys = getConditionKeysForEntity(defaultEntityType);
    const defaultKey = availableKeys[0] || OwnershipConditionKeys.WAREHOUSE_NAME;
    const availableTypes = getConditionTypesForKey(defaultKey);
    const defaultType = availableTypes[0] || OwnershipConditionTypes.EQUALS;

    append({
      entity_type: defaultEntityType,
      condition_key: defaultKey,
      condition_type: defaultType,
      condition_value: "",
      condition_key_value: "",
    });
  };

  const onSubmit = async (data: OwnershipRulesFormValues) => {
    const isValid = await validateForm();
    if (!isValid) {
      toast({
        description: "Please fill in all required fields before creating ownership rules",
        variant: "destructive",
      });
      return;
    }
    createRules(data);
  };

  return {
    form,
    fields,
    remove,
    addOwnership,
    onSubmit,
    validateForm,
    isLoading,
  };
};



